/**
 * API utilities for referral system.
 */
import axios from "axios";
import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";
import { Endpoints } from "@/utils/api/endpoints";

// Payment status constants matching backend payment_status.py
export type RewardStatus =
  | "not_started"
  | "pending"
  | "fraud_review_pending"
  | "fraud_review_passed"
  | "fraud_review_blocked"
  | "delivery_success"
  | "delivery_failed"
  | "cancelled";

// Payment status for referral (computed from rewards)
export type PaymentStatus = "not_started" | "partial" | "paid";

// Signup source types matching backend stripe_constants.py
export type SignupSource =
  "lead" | "installments_promo" | "full_signup" | "pay_by_refund" | null;

// Unified dashboard response structure matching backend API
export interface ReferralDashboardStats {
  total: number; // Total number of referrals
  totalPaidOut: number; // Total amount already paid out (delivered rewards)
  totalPending: number; // Total amount waiting to be paid (pending rewards)
  totalPotentialEarnings: number; // Total potential earnings from all referrals
  leads: number; // Number of referrals that signed up as leads (free tier)
  convertedCustomers: number; // Number of referrals that converted to paying customers
}

// Reward type constants matching backend constants.py
export type RewardType = "promo_signup" | "full_signup" | "promo_complete";

// Delivery method types
export type DeliveryMethod = "EMAIL" | "LINK";

export interface RewardDetail {
  id: string; // Reward ID
  status: RewardStatus;
  amount: number; // Reward amount in dollars
  type: RewardType; // Reward type
  rewardId: string | null; // Tremendous reward ID (null for manual payments)
  orderId: string | null; // Tremendous order ID (null for manual payments)
  deliveryMethod: DeliveryMethod; // Delivery method (EMAIL or LINK)
  createdAt: string | null; // ISO timestamp
}

export interface ReferralDashboardEntry {
  id: string;
  refereeEmail: string | null; // Email of the person who signed up (null if pending)
  refereeName: string | null; // Name of the person who signed up (null if pending or unavailable)
  signupSource: SignupSource; // Signup classification: lead, installments_promo, or full_signup
  paymentStatus: PaymentStatus; // Computed payment status: not_started, partial, or paid
  rewardAmount: number; // Total reward amount in dollars (default 100)
  referrerRewards: RewardDetail[]; // All referrer rewards (may include multiple for installments)
  refereeRewards: RewardDetail[]; // All referee rewards (may include multiple for installments)
  createdAt: string | null; // ISO timestamp
}

export interface ReferralDashboardResponse {
  statistics: ReferralDashboardStats;
  referralLink: string; // Generated referral link from backend
  entries: ReferralDashboardEntry[];
  pagination: {
    total: number;
    page: number;
    pageSize: number; // Backend returns camelCase
    totalPages: number;
  };
}

/**
 * Fetch referral dashboard with statistics and entries (unified endpoint)
 */
export const getReferralDashboard = async (
  page: number = 1,
  pageSize: number = 20
): Promise<ReferralDashboardResponse> => {
  const response = await axios.get(
    `${backend_url}/${Endpoints.referrals.dashboard}`,
    {
      ...AXIOS_CONFIG,
      params: { page, page_size: pageSize },
    }
  );
  return response.data.content;
};

/**
 * Get referrer business branding and details
 */
export const getReferrerBranding = async (referrerId: string) => {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.referrals.branding}`,
      {
        params: { referrerId },
      }
    );
    return response.data.content;
  } catch (error) {
    console.error("Error fetching referrer branding:", error);
    return null;
  }
};

/**
 * Referral slug management
 */
export interface ReferralSlugData {
  referralSlug: string | null; // The custom slug (null if using default)
  effectiveSlug: string; // The actual slug being used (custom or user ID)
  previewUrl: string; // Full preview URL
}

export interface SlugAvailability {
  available: boolean;
  message: string;
}

/**
 * Get the current user's referral slug
 */
export const getReferralSlug = async (): Promise<ReferralSlugData> => {
  const response = await axios.get(
    `${backend_url}/${Endpoints.referrals.slug}`,
    AXIOS_CONFIG
  );
  return response.data.content;
};

/**
 * Check if a referral slug is available
 */
export const checkSlugAvailability = async (
  slug: string
): Promise<SlugAvailability> => {
  const response = await axios.get(
    `${backend_url}/${Endpoints.referrals.slugCheck}`,
    {
      params: { slug },
    }
  );
  return response.data.content;
};

/**
 * Update the current user's referral slug
 * Pass null or empty string to reset to default (user ID)
 */
export const updateReferralSlug = async (
  slug: string | null
): Promise<ReferralSlugData> => {
  const response = await axios.post(
    `${backend_url}/${Endpoints.referrals.slug}`,
    { slug },
    AXIOS_CONFIG
  );
  return response.data.content;
};

/**
 * Generate a Tremendous redemption link for a reward
 */
export const generateRewardLink = async (
  rewardDbId: string
): Promise<{ link: string; rewardId: string }> => {
  const url = `${backend_url}/${Endpoints.referrals.rewardLink}`.replace(
    ":rewardDbId",
    rewardDbId
  );
  const response = await axios.get(url, AXIOS_CONFIG);
  return response.data.content;
};

/**
 * Referral validation types
 */
export interface ValidateReferralResponse {
  valid: boolean;
  referrerId?: string;
  referrerName?: string;
  businessName?: string;
  errorMessage?: string;
  exampleFormat?: string;
}

export interface ReferralExampleResponse {
  exampleFormat: string;
  description: string;
}

/**
 * Validate a referral link and extract referrer information
 * This accepts various referral URL formats and returns validation results
 */
export const validateReferralLink = async (
  referralLink: string
): Promise<ValidateReferralResponse> => {
  const response = await axios.post(
    `${backend_url}/${Endpoints.referrals.validate}`,
    { referralLink }
  );
  return response.data.content;
};

/**
 * Get the current referral link example format for the environment
 * This is useful for showing users what a valid referral link looks like
 */
export const getReferralExample =
  async (): Promise<ReferralExampleResponse> => {
    const response = await axios.get(
      `${backend_url}/${Endpoints.referrals.example}`
    );
    return response.data.content;
  };

/**
 * Onboarding referral types (authenticated endpoints)
 */
export interface ReferralStatusResponse {
  wasReferred: boolean;
  referrerId?: string;
  referrerName?: string;
  businessName?: string;
}

export interface PublicBusinessItem {
  businessId: string;
  businessName: string;
  ownerName?: string;
  ownerId: string;
  city?: string;
  state?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

export interface PublicBusinessesResponse {
  businesses: PublicBusinessItem[];
  total: number;
}

export interface AssociateReferrerResponse {
  success: boolean;
  message: string;
  referralId?: string;
}

/**
 * Check if the current authenticated user was referred by someone
 * This checks if a referral relationship exists where the user is the referee
 */
export const checkReferralStatus =
  async (): Promise<ReferralStatusResponse> => {
    const response = await axios.get(
      `${backend_url}/${Endpoints.referrals.onboardingStatus}`,
      AXIOS_CONFIG
    );
    return response.data.content;
  };

/**
 * Get list of public businesses that users can select as referrers
 * Returns all businesses that have opted to be publicly listed (is_public=True)
 */
export const getPublicBusinesses =
  async (): Promise<PublicBusinessesResponse> => {
    const response = await axios.get(
      `${backend_url}/${Endpoints.referrals.publicBusinesses}`,
      AXIOS_CONFIG
    );
    return response.data.content;
  };

/**
 * Associate the current user with a referrer
 * Creates a referral relationship during onboarding
 */
export const associateReferrer = async (
  referrerId: string
): Promise<AssociateReferrerResponse> => {
  const response = await axios.post(
    `${backend_url}/${Endpoints.referrals.associate}`,
    { referrerId },
    AXIOS_CONFIG
  );
  return response.data.content;
};

/**
 * Save onboarding source request
 */
export interface SaveOnboardingSourceRequest {
  sourceType: string; // 'referrals', 'ads', 'google', 'other', or custom
  referrerId?: string; // Required only if sourceType is 'referrals'
  metadata?: Record<string, any>; // Optional additional metadata
}

/**
 * Save onboarding source response
 */
export interface SaveOnboardingSourceResponse {
  success: boolean;
  message: string;
  sourceId?: string;
}

/**
 * Get onboarding source response
 */
export interface GetOnboardingSourceResponse {
  sourceType: string | null;
  referrerId: string | null;
  metadata: Record<string, any> | null;
}

/**
 * Get the saved onboarding source for the current authenticated user
 * This retrieves where the user came from during signup
 */
export const getOnboardingSource =
  async (): Promise<GetOnboardingSourceResponse> => {
    const response = await axios.get(
      `${backend_url}/${Endpoints.referrals.source}`,
      AXIOS_CONFIG
    );
    return response.data.content;
  };

/**
 * Save the onboarding source for the current authenticated user
 * This captures where the user came from during signup
 */
export const saveOnboardingSource = async (
  request: SaveOnboardingSourceRequest
): Promise<SaveOnboardingSourceResponse> => {
  const response = await axios.post(
    `${backend_url}/${Endpoints.referrals.source}`,
    request,
    AXIOS_CONFIG
  );
  return response.data.content;
};

/**
 * Pay by refund eligibility check
 */
export interface PayByRefundCheckResponse {
  shouldForcePayPerReturn: boolean;
  shouldForcePartialPayment: boolean; // DEPRECATED: Will be removed in future releases
  referrerId: string;
}

/**
 * Check if a referrerId should force Pay Per Return checkout method.
 * This endpoint checks if the referrerId is in the PAY_BY_REFUND_IDS list
 * on the backend, which can be updated without frontend redeployment.
 *
 * @param referrerId - The referrer ID to check
 * @returns Promise with shouldForcePayPerReturn boolean and referrerId
 */
export const checkPayByRefundEligibility = async (
  referrerId: string
): Promise<PayByRefundCheckResponse> => {
  try {
    const response = await axios.get(
      `${backend_url}/api/referrals/check-pay-by-refund/`,
      {
        ...AXIOS_CONFIG,
        params: { referrerId },
      }
    );

    return response.data.content;
  } catch (error) {
    console.error("Error checking pay by refund eligibility:", error);
    // Return false on error to allow normal flow
    return {
      shouldForcePayPerReturn: false,
      shouldForcePartialPayment: false,
      referrerId,
    };
  }
};

/**
 * Export referral CSV parameters
 */
export interface ExportReferralParams {
  columns: string[];
  dateFrom?: Date | null;
  dateTo?: Date | null;
}

/**
 * Export referral data as CSV file
 * Downloads a CSV file with selected columns and optional date filtering
 *
 * @param params - Export parameters including columns and optional date range
 * @returns Promise with Blob containing CSV data
 */
export const exportReferralCSV = async (
  params: ExportReferralParams
): Promise<Blob> => {
  const queryParams: Record<string, string> = {
    columns: params.columns.join(","),
  };

  // Add date filters if provided
  if (params.dateFrom) {
    queryParams.date_from = params.dateFrom.toISOString().split("T")[0]; // YYYY-MM-DD format
  }
  if (params.dateTo) {
    queryParams.date_to = params.dateTo.toISOString().split("T")[0];
  }

  const response = await axios.get(
    `${backend_url}/${Endpoints.referrals.export}`,
    {
      ...AXIOS_CONFIG,
      params: queryParams,
      responseType: "blob",
    }
  );
  return response.data;
};
